/**
 * SystemAlert - Environmental intelligence alert message
 */

import { motion } from 'framer-motion';
import { useChatStore } from '@/stores';
import { cn } from '@/utils/cn';
import type { SystemAlert as SystemAlertType, AlertSeverity } from '@/types';

interface SystemAlertProps {
  alert: SystemAlertType;
}

const SEVERITY_STYLES: Record<AlertSeverity, { bg: string; border: string; icon: string; text: string }> = {
  info: {
    bg: 'bg-hud-primary/10',
    border: 'border-hud-primary/30',
    icon: 'text-hud-primary',
    text: 'text-hud-primary',
  },
  success: {
    bg: 'bg-hud-success/10',
    border: 'border-hud-success/30',
    icon: 'text-hud-success',
    text: 'text-hud-success',
  },
  warning: {
    bg: 'bg-hud-warning/10',
    border: 'border-hud-warning/30',
    icon: 'text-hud-warning',
    text: 'text-hud-warning',
  },
  danger: {
    bg: 'bg-hud-danger/10',
    border: 'border-hud-danger/30',
    icon: 'text-hud-danger',
    text: 'text-hud-danger',
  },
};

export function SystemAlert({ alert }: SystemAlertProps) {
  const removeMessage = useChatStore((state) => state.removeMessage);
  const styles = SEVERITY_STYLES[alert.severity];

  const handleDismiss = () => {
    if (alert.dismissible) {
      removeMessage(alert.id);
    }
  };

  const handleAction = () => {
    if (alert.actionCallback) {
      alert.actionCallback();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={cn(
        'relative rounded-lg border p-3',
        styles.bg,
        styles.border
      )}
    >
      {/* Icon and title row */}
      <div className="flex items-start gap-2">
        <AlertIcon severity={alert.severity} className={cn('w-4 h-4 mt-0.5', styles.icon)} />
        <div className="flex-1 min-w-0">
          <div className={cn('font-hud text-xs uppercase tracking-wider', styles.text)}>
            {alert.title}
          </div>
          <div className="text-sm text-text-primary mt-1 leading-relaxed">
            {alert.content}
          </div>

          {/* Action button */}
          {alert.actionLabel && (
            <button
              onClick={handleAction}
              className={cn(
                'mt-2 px-3 py-1 rounded text-xs font-hud uppercase',
                'border transition-colors',
                styles.border,
                styles.text,
                'hover:bg-white/10'
              )}
            >
              {alert.actionLabel}
            </button>
          )}
        </div>

        {/* Dismiss button */}
        {alert.dismissible && (
          <button
            onClick={handleDismiss}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            <CloseIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Timestamp */}
      <div className="text-[10px] text-text-muted mt-2 text-right">
        {formatTime(alert.timestamp)}
      </div>
    </motion.div>
  );
}

function AlertIcon({ severity, className }: { severity: AlertSeverity; className?: string }) {
  switch (severity) {
    case 'success':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      );
    case 'danger':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
      );
    case 'info':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
      );
  }
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default SystemAlert;
